import { useEffect, useState } from 'react';
import AppHeader from '../../components/AppHeader';
import CatalogPicker from '../../components/CatalogPicker';
import DynamicAttributeForm from '../../components/DynamicAttributeForm';
import { useAuth } from '../../contexts/MemberAuthContext';
import { inventoryApi, chaptersApi, catalogApi } from '../../api';

const STATE_LABELS = { ok: 'V pořádku', damaged: 'Poškozeno', service: 'K revizi', retired: 'Vyřazeno' };
const emptyForm = { selection: null, unit: 'ks', quantity: 0, state: 'ok', attributes: {}, custom_name: '', note: '' };

export default function ChapterInventory() {
  const { member } = useAuth();
  const canManage = ['superadmin', 'chapter_rep'].includes(member.role);
  const isOps = ['superadmin', 'ops'].includes(member.role);

  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState(member.chapter_id || '');
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOps) chaptersApi.list().then((r) => {
      setChapters(r.data);
      if (!chapterId && r.data.length) setChapterId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = () => {
    if (!chapterId) { setItems([]); return; }
    inventoryApi.list(chapterId).then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [chapterId]);

  const resetForm = () => { setForm(emptyForm); setEditId(null); setShowForm(false); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const sel = form.selection;
    const payload = {
      unit: form.unit, quantity: Number(form.quantity) || 0, state: form.state,
      attributes: form.attributes, note: form.note || null,
      catalog_item_type_id: sel?.type === 'catalog' ? sel.itemType.id : null,
      custom_name: sel?.type === 'custom' ? form.custom_name : null,
      custom_category_id: sel?.type === 'custom' ? sel.custom_category_id : null,
    };
    if (!payload.catalog_item_type_id && !payload.custom_name) {
      setError('Vyberte položku z katalogu nebo zadejte vlastní název.');
      return;
    }
    try {
      if (editId) await inventoryApi.update(editId, payload);
      else await inventoryApi.create({ ...payload, chapter_id: chapterId });
      resetForm();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Uložení selhalo.');
    }
  };

  const remove = async (id) => {
    if (!confirm('Opravdu smazat položku?')) return;
    await inventoryApi.delete(id);
    load();
  };

  const itemName = (it) => it.catalog_name || it.custom_name || '—';
  const itemCategory = (it) => it.category_name || it.custom_category_name || '';

  return (
    <div>
      <AppHeader subtitle="Sklad spolku" />
      <div className="container-wide" style={{ paddingTop: 24 }}>

        {isOps && (
          <div className="form-group" style={{ maxWidth: 360 }}>
            <label>Oblastní spolek</label>
            <select className="form-input" value={chapterId} onChange={(e) => setChapterId(e.target.value)}>
              {chapters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {!chapterId && <div className="alert alert-info">Nemáte přiřazený žádný spolek.</div>}

        {chapterId && (
          <>
            <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2>Materiál ({items.length})</h2>
              <div className="flex gap-8">
                {items.length > 0 && <a className="btn btn-outline btn-sm" href={inventoryApi.csvUrl(chapterId)} download>⬇ CSV</a>}
                {canManage && !showForm && (
                  <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Přidat položku</button>
                )}
              </div>
            </div>

            {showForm && canManage && (
              <div className="card mb-16">
                <h3 className="mb-16">{editId ? 'Upravit položku' : 'Nová položka'}</h3>
                <form onSubmit={submit}>
                  <CatalogPicker value={form.selection} onSelect={(sel) => {
                    setForm((f) => ({
                      ...f, selection: sel,
                      unit: sel?.type === 'catalog' ? sel.itemType.unit : f.unit,
                      attributes: sel?.type === 'catalog' ? {} : f.attributes,
                    }));
                  }} />

                  {form.selection?.type === 'custom' && (
                    <div className="form-group">
                      <label>Název vlastní položky</label>
                      <input className="form-input" value={form.custom_name} onChange={(e) => setForm({ ...form, custom_name: e.target.value })} required />
                    </div>
                  )}

                  {form.selection?.type === 'catalog' && (
                    <DynamicAttributeForm schema={form.selection.itemType.attribute_schema}
                      values={form.attributes} onChange={(attributes) => setForm({ ...form, attributes })} />
                  )}

                  <div className="flex gap-8">
                    <div className="form-group flex-1">
                      <label>Množství</label>
                      <input type="number" min="0" step="any" className="form-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                    </div>
                    <div className="form-group flex-1">
                      <label>Jednotka</label>
                      <input className="form-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
                    </div>
                    <div className="form-group flex-1">
                      <label>Stav</label>
                      <select className="form-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}>
                        {Object.entries(STATE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Poznámka</label>
                    <input className="form-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                  </div>
                  {error && <div className="alert alert-warning mb-16">{error}</div>}
                  <div className="flex gap-8">
                    <button type="submit" className="btn btn-primary flex-1">{editId ? 'Uložit' : 'Přidat'}</button>
                    <button type="button" className="btn btn-outline" onClick={resetForm}>Zrušit</button>
                  </div>
                </form>
              </div>
            )}

            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Položka</th><th>Kategorie</th><th>Množství</th><th>Stav</th><th>Poznámka</th>{canManage && <th></th>}</tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id}>
                      <td>{itemName(it)}{!it.catalog_item_type_id && <span className="badge badge-none" style={{ marginLeft: 6 }}>vlastní</span>}</td>
                      <td>{itemCategory(it)}</td>
                      <td>{it.quantity} {it.unit}</td>
                      <td>{STATE_LABELS[it.state] || it.state}</td>
                      <td className="text-muted" style={{ fontSize: '0.85rem' }}>{it.note || ''}</td>
                      {canManage && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-outline btn-sm" onClick={async () => {
                            setEditId(it.id); setShowForm(true); setError('');
                            let selection;
                            if (it.catalog_item_type_id) {
                              let schema = [];
                              try { schema = (await catalogApi.itemType(it.catalog_item_type_id)).data.attribute_schema || []; } catch { /* ignore */ }
                              selection = { type: 'catalog', itemType: { id: it.catalog_item_type_id, unit: it.unit, attribute_schema: schema } };
                            } else {
                              selection = { type: 'custom', custom_category_id: it.custom_category_id };
                            }
                            setForm({
                              selection,
                              unit: it.unit, quantity: it.quantity, state: it.state,
                              attributes: it.attributes || {}, custom_name: it.custom_name || '', note: it.note || '',
                            });
                          }}>Upravit</button>{' '}
                          <button className="btn btn-danger btn-sm" onClick={() => remove(it.id)}>Smazat</button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={canManage ? 6 : 5} className="text-muted text-center">Sklad je prázdný.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
